<script lang="ts">
    import type { LanguageDataCarrierV2 } from "@app/models/api";
    import { sendUIInteractionAnalyticsEvent } from "@app/services/analytics/analytics-send-event";
    import {
        dictionary,
        updateDictionary,
    } from "@app/services/currentDictionary.service";
    import { getLanguages } from "@app/services/glosbe-dictionary-api/get-languages";
    import {
        COMMON__allOtherLanguages__title,
        COMMON__popular,
        COMMON__recentlyUsedLanguages,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import {
        ItemsListManager,
        ItemsListManagerStateCreator,
        setItemsListManager,
    } from "../../ItemsList/ItemsListManager";
    import { hideChangeLanguageWidget } from "../ChangeDictionary.store";
    import ChangeLanguageInlineLanguagesList from "./ChangeLanguageInlineLanguagesList.svelte";
    import { fade } from "svelte/transition";

    export let languageType: string;
    export let open: boolean = false;
    export let activator: HTMLElement;

    const manager = new ItemsListManager();
    setItemsListManager(manager);

    async function updateLanguagesList(query: string) {
        manager.setInProgress(true);

        let languages = await getLanguages({ query });

        const listItemsCreator = new ItemsListManagerStateCreator();
        if (languages) {
            if (languages.recentLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__recentlyUsedLanguages),
                    languages.recentLanguages
                );
            }
            const langCodesToFilterOut = {};
            languages.recentLanguages.forEach(lang => langCodesToFilterOut[lang.code] = true);

            if (languages.popularLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__popular),
                    languages.popularLanguages.filter(lang => !langCodesToFilterOut[lang.code])
                );
            }
            languages.popularLanguages.forEach(lang => langCodesToFilterOut[lang.code] = true);
            if (languages.otherLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__allOtherLanguages__title),
                    languages.otherLanguages.filter(lang => !langCodesToFilterOut[lang.code])
                );
            }
        }
        manager.setInProgress(false);
        manager.setState(listItemsCreator.state);
    }

    updateLanguagesList("");

    async function onInput(event: Event) {
        event.preventDefault();
        const query = (event.target as HTMLInputElement).value;
        updateLanguagesList(query);
    }

    manager.onItemSelected((selectedLanguage: LanguageDataCarrierV2) => {
        // PS selectedLanguage is not of type LanguageDataCarrierV2, but rather NamedLanguage
        // it lacks information about the special characters; the result is that keyboard button get's removed
        // even if it should be there. It's why it is added artificially below

        let { l1, l2 } = $dictionary;
        if (languageType === "source") {
            l1 = selectedLanguage;
            // we don't know the  hasSpecialChars here, it's better to display it if not needed
            // than to hide it when it may be useful; optionally we could ask backend here for full LanguageDataCarrierV2
            l1.hasSpecialChars = true;
        } else {
            l2 = selectedLanguage;

            // we don't know the  hasSpecialChars here, it's better to display it if not needed
            // than to hide it when it may be useful; optionally we could ask backend here for full LanguageDataCarrierV2
            l2.hasSpecialChars = true;
        }

        updateDictionary({ l1, l2 });
        hideChangeLanguageWidget();
        const phrase = document.querySelector("[data-element=source-phrase]");
        let url = `/${l1.code}/${l2.code}`;
        if (phrase) {
            url = `${url}/${phrase.textContent.trim()}`;
        }
        open = false;
        //window.location.href = url;
    });

    let containerElement: HTMLSpanElement;

    function closeOnCLickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (open && containerElement && !containerElement.contains(target)) {
            open = false;
        }
    }
    function onClose() {
        open = false;
        updateLanguagesList("");
    }

    let prevOpen;

    const onKeydown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            open == false;
        }
    };

    $: {
        if (open != prevOpen) {
            prevOpen = open;
            sendUIInteractionAnalyticsEvent({
                action: "search-language-inline",
                label: open ? "open" : "close",
            });
        }
    }

    const isActivatorMain = activator.dataset["activatorType"] === "main";

    const languageNameClass = isActivatorMain
        ? "flex px-3 text-base font-medium leading-5 text-white py-1 justify-center"
        : "flex justify-center text-xs text-white focus:outline-none focus:text-white transition duration-150 ease-in-out pt-1";
</script>

<svelte:window on:click={closeOnCLickOutside} />
{#if open}
    <span
        data-element="change-language-inline"
        transition:fade={{ duration: 150 }}
        bind:this={containerElement}
    >
        <div
            class="absolute inset-0 text-gray-900 rounded change-language-inline bg-primary-600 "
        >
            <div class={languageNameClass}>
                <span class="dictionary__source__name truncate">
                    {languageType === "source"
                        ? $dictionary.l1.nameForDictionarySource ||
                          $dictionary.l1.name
                        : $dictionary.l2.name}
                </span>
                <span class="icon-arrow-down" />
            </div>
        </div>
        <ChangeLanguageInlineLanguagesList
            {onKeydown}
            {onInput}
            {onClose}
            {languageType}
        />
    </span>
{/if}

<style>
    .change-language-inline {
        top: -0.25rem;
        bottom: -0.5rem;
    }
</style>
